import { isDate } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';

import { useLocation } from 'src/shared/hooks';

export const useFilterParams = <
  T extends (params: URLSearchParams) => ReturnType<T>,
>(
  createFilterFn: T,
): ReturnType<T> | null => {
  const { searchParams } = useLocation();
  const filtersParams = useMemo<ReturnType<T>>(
    () => createFilterFn(searchParams),
    [createFilterFn, searchParams],
  );

  return filtersParams;
};
const stringify = (value: any) => {
  if (typeof value === 'undefined' || value === null) return '';
  if (typeof value === 'string') return value;
  if (isDate(value)) return value.toISOString();
  return JSON.stringify(value);
};
/**
 * A hook to update the URL with the new filter values. It will take the
 * `valuesToOmit` and compare to the current values to determine what values
 * should be added to the URL.
 * @param filterParams - Values to be updated in the URL
 * @param valuesToOmit - Default values for the filters
 */
export const useSetFilterParams = <T>(
  filterParams?: Partial<T> | null,
  valuesToOmit?: T,
) => {
  const { href } = useLocation();
  const history = useHistory();

  return useCallback(
    (nextFilterParams: Partial<T> = {}) => {
      const nextHref = new URL(href);
      // Delete all current filters
      if (filterParams) {
        Object.keys(filterParams).forEach(key => {
          nextHref.searchParams.delete(key);
        });
      }

      // Update the URL with the new params
      Object.entries(nextFilterParams).forEach(([key, value]) => {
        // No need to set the value if it's null or undefined
        if (value === null || typeof value === 'undefined') return;
        const valueToOmit = valuesToOmit?.[key as keyof T];
        // If the value is an array, append each value to the search params
        // Otherwise set the value as a single search param
        if (Array.isArray(value)) {
          // if the array is the same as the default value, don't add it to the URL
          if (
            Array.isArray(valueToOmit) &&
            value.length === valueToOmit.length &&
            value.every(v => valueToOmit?.includes(v))
          ) {
            return;
          }
          value.forEach(v => nextHref.searchParams.append(key, stringify(v)));
        } else {
          if (value === valueToOmit) return;
          nextHref.searchParams.set(key, stringify(value));
        }
      });

      history.push({
        pathname: nextHref.pathname,
        search: nextHref.search,
      });
    },
    [href, filterParams, valuesToOmit, history],
  );
};
